import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './HealthCountdownFlyout.css';

/**
 * Emits a flyout tick whenever displayed HP drops during countdown pulse.
 */
export function useHealthCountdownFlyoutTick(health, isPulsing) {
  const prevHealthRef = useRef(health);
  const [tick, setTick] = useState(null);

  useEffect(() => {
    if (!isPulsing || typeof health !== 'number') {
      prevHealthRef.current = health;
      setTick(null);
      return;
    }

    if (
      typeof prevHealthRef.current === 'number'
      && health < prevHealthRef.current
    ) {
      setTick({
        key: `${Date.now()}-${health}`,
        value: health,
      });
    }
    prevHealthRef.current = health;
  }, [health, isPulsing]);

  return tick;
}

function measureAnchorCenter(anchorRef) {
  const el = anchorRef?.current;
  if (!el) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

/**
 * Large HP number anchored to the order health badge; scales up and flies toward the player.
 * @param {React.RefObject<HTMLElement>} anchorRef - The health indicator element on the order card
 */
export function HealthCountdownFlyout({ value, zoneName, anchorRef }) {
  const [origin, setOrigin] = useState(null);

  useEffect(() => {
    setOrigin(measureAnchorCenter(anchorRef));
  }, [anchorRef, value]);

  if (typeof value !== 'number' || !origin || typeof document === 'undefined') {
    return null;
  }

  const fromOpponent = zoneName?.startsWith('opponent_');
  const variantClass = fromOpponent
    ? 'health-countdown-flyout--from-opponent'
    : 'health-countdown-flyout--from-friendly';

  return createPortal(
    <div
      className={`health-countdown-flyout health-countdown-flyout--portal ${variantClass}`}
      style={{
        left: `${origin.x}px`,
        top: `${origin.y}px`,
      }}
      aria-hidden
    >
      <span className="health-countdown-flyout__ring" />
      <span className="health-countdown-flyout__value">{value}</span>
    </div>,
    document.body
  );
}

export default HealthCountdownFlyout;
